import React, { useState, useEffect } from 'react';
import {
  Settings,
  X,
  UserPlus,
  User,
  Phone,
  MapPin,
  Calendar,
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { useLanguageContext } from '../navigations/homenav';
import VoiceInputButton from './VoiceInputButton';

const FloatingSelfRegisterForm = () => {
  const { t } = useLanguageContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Automatically expand and collapse the button to draw attention
    const expandInterval = setInterval(() => {
      if (!isOpen) {
        setIsExpanded(true);
        setTimeout(() => setIsExpanded(false), 4000); // Show for 4 seconds
      }
    }, 10000); // Repeat every 10 seconds

    return () => clearInterval(expandInterval);
  }, [isOpen]);

  const disabilityTypes = [
    t('visualImpairment'),
    t('hearingImpairment'),
    t('PhysicalMobilityImpairment'),
    t('IntellectualDisability'),
    t('AutismNeurodivergence'),
    t('mentalHealthCondition'),
    t('chronicIllness'),
    t('speechImpairment'),
    t('other'),
  ];

  const subcities = [
    "አዲስ ከተማ", "አቃቂ ቃሊቲ", "አራዳ", "ቦሌ", "ጉለሌ", 
    "ቂርቆስ", "ኮልፌ ቀራኒዮ", "ለሚ ኩራ", "ልደታ", "ንፋስ ስልክ ላፍቶ", "የካ"
  ];

  const [formData, setFormData] = useState({
    fullName: '',
    gender: 'Male',
    age: '',
    phoneNumber: '',
    disabilityDescription: '',
    city: 'Addis Ababa',
    subcity: subcities[0],
    wereda: '',
    houseNumber: '',
    idAttachment: null,
    message: ''
  });

  // Multiple disability types state (for self registration)
  const [selectedDisabilityTypes, setSelectedDisabilityTypes] = useState(['']);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = t('fullNameRequired');
    if (!formData.age || formData.age < 1 || formData.age > 120) newErrors.age = t('validAgeRequired');
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = t('phoneNumberRequired');
    if (!formData.city.trim()) newErrors.city = t('cityRequired');
    if (!formData.wereda.trim()) newErrors.wereda = t('weredaRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { id, value, files } = e.target;
    
    if (id === 'idAttachment') {
      setFormData(prev => ({ ...prev, [id]: files[0] || null }));
    } else {
      setFormData(prev => ({ ...prev, [id]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[id]) {
      setErrors(prev => ({ ...prev, [id]: '' }));
    }
  };

  const handleVoiceInput = (fieldName, transcript) => {
    setFormData(prev => ({ ...prev, [fieldName]: transcript }));
    
    // Clear error when voice input is used
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      gender: 'Male',
      age: '',
      phoneNumber: '',
      disabilityDescription: '',
      city: 'Addis Ababa',
      subcity: subcities[0],
      wereda: '',
      houseNumber: '',
      idAttachment: null,
      message: ''
    });
    setSelectedDisabilityTypes(['']);
    setErrors({});
    setSubmitStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Build disabilityType string from multiple selections
      const disabilityTypeString = selectedDisabilityTypes
        .map((val) => (val || '').trim())
        .filter(Boolean)
        .join(', ');

      // Get current user from localStorage
     
      // Prepare form data for submission
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] && key !== 'idAttachment' && key !== 'disabilityDescription') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append computed disabilityType string (from multiple selections)
      if (disabilityTypeString) {
        formDataToSend.append('disabilityType', disabilityTypeString);
      }

      // Append disability description if provided
      if (formData.disabilityDescription) {
        formDataToSend.append('disabilityDescription', formData.disabilityDescription);
      }

      // Add the ID attachment if exists
      if (formData.idAttachment) {
        formDataToSend.append('idAttachment', formData.idAttachment);
      }


      // Submit to backend
      const response = await axios.post(
        'https://aaapd.org.et/gallery_backend/api/addSelfRegisteredMember.php',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.status === 'success') {
        setSubmitStatus('success');
        setTimeout(() => {
          resetForm();
          setIsOpen(false);
        }, 2000);
      } else {
        setSubmitStatus('error');
        setErrors({ submit: response.data.message || t('submitFormFailed') });
      }

    } catch (error) {
      console.error('Failed to submit form:', error);
      setSubmitStatus('error');
      setErrors({ submit: t('submitFormFailed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleForm = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  return (
    <>
      {/* Enhanced Floating Button */}
      <button
        onClick={toggleForm}
        className={`fixed top-2/3 right-0 -translate-y-1/2 bg-gradient-to-l from-teal-600 to-teal-700 text-white flex items-center h-14 rounded-l-xl shadow-xl z-[100] hover:from-teal-700 hover:to-teal-800 transition-all duration-500 ease-in-out transform hover:scale-105`}
        style={{ width: isExpanded ? '240px' : '60px' }}
        aria-label={t('openSelfRegistrationForm')}
      >
        <div className="flex items-center absolute right-4">
          <span 
            className={`mr-3 whitespace-nowrap font-semibold transition-opacity duration-300 ease-in-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}>
            {t('selfRegisterHere')}
          </span>
          <UserPlus className={`transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} size={24} />
        </div>
      </button>

      {/* Enhanced Modal Form */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-start justify-center p-4 pt-20">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg md:max-w-2xl lg:max-w-4xl max-h-[85vh] flex flex-col overflow-hidden relative z-[10000]">
          
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <UserPlus className="w-6 h-6" />
                <h2 className="text-2xl font-bold">{t('selfRegistrationForm')}</h2>
              </div>
              <button
                onClick={toggleForm}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                aria-label={t('closeForm')}
              >
                <X size={24} />
              </button>
            </div>
            <p className="text-teal-100 mt-2">{t('selfRegistrationDesc')}</p>
          </div>

          {/* Form Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-green-800 font-semibold">{t('registrationSuccessful')}</p>
                  <p className="text-green-700 text-sm">{t('registrationSuccessDesc')}</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-red-800 font-semibold">{t('registrationFailed')}</p>
                  <p className="text-red-700 text-sm">{errors.submit}</p>
                </div>
              </div>
            )}

            <form className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" onSubmit={handleSubmit}>
              
              {/* Personal Information Section */}
              <div className="lg:col-span-3">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <User className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">{t('personalInformation')}</h3>
                </div>
              </div>

              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fullName')} *
                </label>
                <div className="relative">
                  <input 
                    type="text" 
                    id="fullName" 
                    value={formData.fullName} 
                    onChange={handleChange} 
                    className={`w-full border rounded-lg shadow-sm py-2 px-3 pr-12 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                      errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder={t('fullNamePlaceholder')}
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <VoiceInputButton 
                      onTranscript={(transcript) => handleVoiceInput('fullName', transcript)}
                      language="en-US"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                {errors.fullName && <p className="text-red-600 text-sm mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('gender')}
                </label>
                <select 
                  id="gender" 
                  value={formData.gender} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('age')} *
                </label>
                <input 
                  type="number" 
                  id="age" 
                  value={formData.age} 
                  onChange={handleChange} 
                  className={`w-full border rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                    errors.age ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder={t('agePlaceholder')}
                  min="1"
                  max="120"
                />
                {errors.age && <p className="text-red-600 text-sm mt-1">{errors.age}</p>}
              </div>

              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {t('phoneNumber')} *
                  </div>
                </label>
                <div className="relative">
                  <input 
                    type="tel" 
                    id="phoneNumber" 
                    value={formData.phoneNumber} 
                    onChange={handleChange} 
                    className={`w-full border rounded-lg shadow-sm py-2 px-3 pr-12 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                      errors.phoneNumber ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder={t('phoneNumberPlaceholder')}
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <VoiceInputButton 
                      onTranscript={(transcript) => handleVoiceInput('phoneNumber', transcript)}
                      language="en-US"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                {errors.phoneNumber && <p className="text-red-600 text-sm mt-1">{errors.phoneNumber}</p>}
              </div>

              {/* Disability Info Section */}
              <div className="lg:col-span-3">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200 mt-6">
                  <Calendar className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">{t('disabilityInformation') || 'Disability Info'}</h3>
                </div>
              </div>

              {selectedDisabilityTypes.map((val, idx) => (
                <div key={idx} className="col-span-1 md:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('disabilityType')} {idx + 1}
                      </label>
                      <select
                        value={val}
                        onChange={(e) => {
                          const next = [...selectedDisabilityTypes];
                          next[idx] = e.target.value;
                          setSelectedDisabilityTypes(next);
                        }}
                        className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                      >
                        <option value="">{t('selectDisabilityType') || 'Select Disability Type'}</option>
                        {disabilityTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    {selectedDisabilityTypes.length > 1 && (
                      <div className="flex md:justify-start">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDisabilityTypes((prev) => prev.filter((_, i) => i !== idx));
                          }}
                          className="px-4 py-2 mt-6 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                        >
                          {t('remove') || 'Remove'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="col-span-1 md:col-span-2">
                <button
                  type="button"
                  onClick={() => setSelectedDisabilityTypes((prev) => [...prev, ''])}
                  className="px-4 py-2 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100"
                >
                  {t('addDisabilityType') || 'Add disability type'}
                </button>
              </div>

              <div className="md:col-span-2 lg:col-span-3">
                <label htmlFor="disabilityDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('disabilityDescription') || 'Disability Description'}
                </label>
                <textarea
                  id="disabilityDescription"
                  rows="3"
                  value={formData.disabilityDescription}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition resize-none"
                  placeholder={t('disabilityDescription') || 'Describe your disability (optional)'}
                />
              </div>

              {/* Address Information Section */}
              <div className="lg:col-span-3">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200 mt-6">
                  <MapPin className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">{t('addressInformation')}</h3>
                </div>
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('city')} *
                </label>
                <div className="relative">
                  <input 
                    type="text" 
                    id="city" 
                    value={formData.city}
                    readOnly
                    className="w-full border rounded-lg shadow-sm py-2 px-3 bg-gray-100 text-gray-700 cursor-not-allowed"
                  />
                </div>
                {errors.city && <p className="text-red-600 text-sm mt-1">{errors.city}</p>}
              </div>

              <div>
                <label htmlFor="subcity" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('subcity')}
                </label>
                <select 
                  id="subcity" 
                  value={formData.subcity} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                >
                  {subcities.map(city => <option key={city} value={city}>{city}</option>)}
                </select>
              </div>

              <div>
                <label htmlFor="wereda" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('wereda')} *
                </label>
                <div className="relative">
                  <input 
                    type="text" 
                    id="wereda" 
                    value={formData.wereda} 
                    onChange={handleChange} 
                    className={`w-full border rounded-lg shadow-sm py-2 px-3 pr-12 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                      errors.wereda ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder={t('weredaPlaceholder')}
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <VoiceInputButton 
                      onTranscript={(transcript) => handleVoiceInput('wereda', transcript)}
                      language="en-US"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                {errors.wereda && <p className="text-red-600 text-sm mt-1">{errors.wereda}</p>}
              </div>

              <div>
                <label htmlFor="houseNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('houseNumber')} *
                </label>
                <div className="relative">
                  <input 
                    type="text" 
                    id="houseNumber" 
                    value={formData.houseNumber} 
                    onChange={handleChange} 
                    className={`w-full border rounded-lg shadow-sm py-2 px-3 pr-12 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                      errors.houseNumber ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder={t('houseNumberPlaceholder')}
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <VoiceInputButton 
                      onTranscript={(transcript) => handleVoiceInput('houseNumber', transcript)}
                      language="en-US"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                {errors.houseNumber && <p className="text-red-600 text-sm mt-1">{errors.houseNumber}</p>}
              </div>

              <div>
                <label htmlFor="idAttachment" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('idAttachment')}
                </label>
                <input 
                  type="file" 
                  id="idAttachment" 
                  onChange={handleChange} 
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                <p className="text-xs text-gray-500 mt-1">Supported formats: PDF, JPG, PNG, DOC, DOCX (Max 5MB)</p>
              </div>

              {/* Message Section */}
              <div className="lg:col-span-3">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200 mt-6">
                  <MessageSquare className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">{t('additionalInformation')}</h3>
                </div>
              </div>

              <div className="md:col-span-2 lg:col-span-3">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('messageDescription')}
                </label>
                <div className="relative">
                  <textarea 
                    id="message" 
                    rows="4" 
                    value={formData.message} 
                    onChange={handleChange} 
                    className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 pr-12 focus:outline-none focus:ring-2 focus:ring-teal-500 transition resize-none"
                    placeholder={t('messagePlaceholder')}
                  />
                  <div className="absolute right-2 top-2">
                    <VoiceInputButton 
                      onTranscript={(transcript) => handleVoiceInput('message', transcript)}
                      language="en-US"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="md:col-span-2 lg:col-span-3 flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  type="button"
                  onClick={toggleForm}
                  className="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-semibold py-2 px-6 rounded-lg hover:from-teal-700 hover:to-teal-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('submitting')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('submitRegistration')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}
    </>
  );
};

export default FloatingSelfRegisterForm;
